<template>
  <Teleport to="body">
    <div
        v-if="show"
        class="modal-overlay"
        @click.self="$emit('close')"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <font-awesome-icon icon="fa-solid fa-circle-info" class="me-2" />
            Szczegóły rezerwacji
          </h5>
          <button
              type="button"
              class="btn-close"
              :class="{ 'btn-close-white': isDarkMode }"
              @click="$emit('close')"
          ></button>
        </div>
        <div class="modal-body" v-if="!cancellationSuccessful">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-warning" role="status">
              <span class="visually-hidden">Ładowanie...</span>
            </div>
            <p class="mt-3 text-muted">Pobieranie szczegółów...</p>
          </div>
          <div v-else-if="error" class="alert alert-danger d-flex align-items-center" role="alert">
            <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="me-2" />
            <div>{{ error }}</div>
          </div>
          <div v-else-if="details" class="details-list">
            <div class="detail-item">
              <div class="detail-icon">
                <font-awesome-icon icon="fa-solid fa-bullseye" />
              </div>
              <div class="detail-content">
                <label>Stanowisko</label>
                <span>{{ spaceName || details.space_id }}</span>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-icon">
                <font-awesome-icon icon="fa-solid fa-calendar-day" />
              </div>
              <div class="detail-content">
                <label>Data</label>
                <span>{{ formatDate(details.start_date) }}</span>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-icon">
                <font-awesome-icon icon="fa-solid fa-clock" />
              </div>
              <div class="detail-content">
                <label>Czas trwania</label>
                <span>{{ formatTime(details.start_date) }} - {{ formatTime(details.end_date) }}</span>
              </div>
            </div>

            <div v-if="details.amount?.Valid" class="detail-item">
              <div class="detail-icon">
                <font-awesome-icon icon="fa-solid fa-money-bill-wave" />
              </div>
              <div class="detail-content">
                <label>Kwota</label>
                <span class="fw-bold text-success">{{ details.amount.Float64 }} {{ details.currency?.String }}</span>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-icon">
                <font-awesome-icon icon="fa-solid fa-credit-card" />
              </div>
              <div class="detail-content">
                <label>Metoda płatności</label>
                <span :class="{'text-danger': !details.paid, 'text-secondary': details.paid}">
                  {{ formatPaymentMethod(details.paid) }}
                </span>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-icon">
                <font-awesome-icon icon="fa-solid fa-receipt" />
              </div>
              <div class="detail-content">
                <label>Status płatności</label>
                <span :class="{'text-danger': !details.paid_at.Valid, 'text-success': details.paid_at.Valid}">
                  {{ details.paid_at.Valid ? 'Opłacone' : 'Nieopłacone' }}
                </span>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-4 text-muted">
            <font-awesome-icon icon="fa-solid fa-magnifying-glass" size="2x" class="mb-2 opacity-50" />
            <p>Brak danych do wyświetlenia</p>
          </div>
        </div>
        <div v-else>
          <div class="text-center py-4 text-muted">
            <font-awesome-icon icon="fa-solid fa-check" size="2x" class="mb-2 opacity-50" />
            <p>Rezerwacja anulowana!</p>
          </div>
        </div>
        <div class="modal-footer border-0">
          <div class="px-4 me-auto col-8">
            <div v-if="confirmCancellation && !cancellationSuccessful" class="text-center alert alert-warning">
              <p>Czy na pewno chcesz anulować rezerwację?</p>
              <button
                  type="button"
                  class="btn btn-success px-3 me-4"
                  @click="cancelReservation"
                  :disabled="isCancelling"
              >
                <font-awesome-icon icon="fa-solid fa-check" class="opacity-50" /> Tak
              </button>
              <button
                  type="button"
                  class="btn btn-danger px-3 me-auto"
                  @click="hideCancellationConfirmation"
                  :disabled="isCancelling"
              >
                <font-awesome-icon icon="fa-solid fa-xmark" class="opacity-50" /> Nie
              </button>
            </div>
            <button
                v-else-if="!cancellationSuccessful && !loading"
                type="button"
                class="btn btn-outline-warning px-4 me-auto"
                @click="showCancellationConfirmation"
                :disabled="isCancelling"
            >
              <span v-if="isCancelling" class="spinner-border spinner-border-sm me-2"></span>
              <font-awesome-icon icon="fa-solid fa-xmark" /> Anuluj rezerwację
            </button>
          </div>
          <button
              type="button"
              class="btn btn-outline-secondary px-4"
              @click="$emit('close')"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { fetchWrapper } from "@/wrappers/api/fetchWrapper";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

export default {
  name: 'ReservationDetails',
  components: {
    FontAwesomeIcon
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    reservationId: {
      type: [Number, String],
      default: null
    },
    spaceName: {
      type: String,
      default: ''
    }
  },
  emits: ['close'],
  data() {
    return {
      details: null,
      loading: false,
      error: null,
      confirmCancellation: false,
      isCancelling: false,
      cancellationSuccessful: false,
      cancellationError: null,
    }
  },
  computed: {
    isDarkMode() {
      return document.documentElement.getAttribute('data-bs-theme') === 'dark';
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.reservationId) {
        this.fetchDetails();
      } else if (!newVal) {
        this.details = null;
        this.error = null;
      }
    }
  },
  methods: {
    async fetchDetails() {
      if (!this.reservationId) {
        this.error = 'Brak ID rezerwacji';
        return;
      }

      this.loading = true;
      this.error = null;
      this.details = null;
      this.confirmCancellation = false;

      try {
        const response = await fetchWrapper.get(`/reservations/${this.reservationId}`);
        this.details = response.reservationDetails
      } catch (err) {
        this.error = err || 'Nie udało się pobrać szczegółów rezerwacji';
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('pl-PL');
    },
    formatTime(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    },
    formatPaymentMethod(method) {
      if (method === null || method === undefined) return '-';
      const translations = {
        'pay_at_location': 'Płatność na miejscu',
        'p24': 'Przelewy24 (Online)'
      };
      return translations[method] || method;
    },
    showCancellationConfirmation() {
      this.confirmCancellation = true;
    },
    hideCancellationConfirmation() {
      this.confirmCancellation = false;
    },
    async cancelReservation() {
      this.isCancelling = true;
      this.cancellationError = null;

      try {
        const body = {
          uuid: this.reservationId
        };

        console.log(body);

        await fetchWrapper.post('/reservations/cancel', body);

        this.$emit('reservation-cancelled'); // Notify parent to refresh data
        this.cancellationSuccessful = true

        // Close modal after 5 seconds
        setTimeout(() => {
          this.showModal = false;
          this.cancellationSuccessful = false;
          this.$emit('close')
        }, 5000);

      } catch (err) {
        this.cancellationError = err;
      } finally {
        this.isCancelling = false;
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-content {
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 1rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--bs-border-color);
  background-color: var(--bs-tertiary-bg);
}

.modal-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #6596d4;
  margin: 0;
  display: flex;
  align-items: center;
}

.modal-body {
  padding: 1.5rem;
}

.details-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.detail-icon {
  width: 38px;
  height: 38px;
  background-color: var(--bs-secondary-bg);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f0c040;
  flex-shrink: 0;
  font-size: 1.1rem;
}

.detail-content {
  display: flex;
  flex-direction: column;
  min-width: 0; /* Prevents flex children from overflowing */
}

.detail-content label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--bs-secondary-color);
  font-weight: 700;
  margin-bottom: 0.1rem;
}

.detail-content span {
  font-size: 0.95rem;
  color: var(--bs-body-color);
  font-weight: 500;
  word-break: break-word;
}

.modal-footer {
  padding: 1rem 1.5rem;
}

/* Light theme adjustments */
[data-bs-theme="light"] .modal-content {
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1);
}

[data-bs-theme="light"] .detail-icon {
  background-color: #f8f9fa;
}

/* Responsive */
@media (max-width: 576px) {
  .modal-content {
    border-radius: 0.75rem;
  }

  .modal-header, .modal-body, .modal-footer {
    padding: 1rem;
  }
}
</style>